import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShoppingBag, Check, ShoppingCart, Minus, Plus, Trash2, CreditCard } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SpecialListPage = () => {
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [miniCart, setMiniCart] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [deliveryInfo, setDeliveryInfo] = useState({
    delivery_date: "",
    delivery_time: "",
    delivery_comune: "",
    delivery_zone: "",
  });
  const [zones, setZones] = useState([]);

  useEffect(() => {
    fetchList();
    fetchZones();
  }, []);

  const fetchList = async () => {
    try {
      const token = localStorage.getItem("customer_token");
      const res = await axios.get(`${API}/customer/special-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setList(res.data);
    } catch {
      toast.error("Errore nel caricamento della lista");
    } finally {
      setLoading(false);
    }
  };

  const fetchZones = async () => {
    try {
      const res = await axios.get(`${API}/delivery-zones`);
      setZones(res.data || []);
    } catch { /* ignore */ }
  };

  const addToMiniCart = (item) => {
    const remaining = item.quantity_total - item.quantity_ordered;
    if (remaining <= 0) return;
    
    const existing = miniCart.find(c => c.product_id === item.product_id);
    if (existing) {
      if (existing.quantity >= remaining) {
        toast.error("Quantità massima raggiunta per questo prodotto");
        return;
      }
      setMiniCart(miniCart.map(c => 
        c.product_id === item.product_id ? { ...c, quantity: c.quantity + 1 } : c
      ));
    } else {
      setMiniCart([...miniCart, {
        product_id: item.product_id,
        name: item.name,
        price: item.price,
        quantity: 1
      }]);
    }
  };

  const updateMiniCartQty = (productId, delta) => {
    const item = list?.items?.find(i => i.product_id === productId);
    const remaining = item ? item.quantity_total - item.quantity_ordered : 0;
    
    setMiniCart(miniCart.map(c => {
      if (c.product_id === productId) {
        const newQty = Math.min(c.quantity + delta, remaining);
        return newQty > 0 ? { ...c, quantity: newQty } : c;
      }
      return c;
    }).filter(c => c.quantity > 0));
  };

  const removeMiniCart = (productId) => setMiniCart(miniCart.filter(c => c.product_id !== productId));

  const miniCartTotal = miniCart.reduce((sum, c) => sum + c.price * c.quantity, 0);
  const maxAmount = 15;
  const isOverLimit = miniCartTotal > maxAmount;

  const handleOrder = async () => {
    if (miniCart.length === 0) { toast.error("Seleziona almeno un prodotto"); return; }
    if (isOverLimit) { toast.error(`Il totale supera il limite di €${maxAmount.toFixed(2)}`); return; }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("customer_token");
      const res = await axios.post(`${API}/customer/special-list/confirm-order`, {
        items: miniCart,
        origin_url: window.location.origin,
        ...deliveryInfo
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (res.data.success && res.data.checkout_url) {
        // Redirect a SumUp
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione ordine");
    } finally {
      setSubmitting(false);
    }
  };

  const timeSlots = ["10:00-12:00", "12:00-14:00", "14:00-16:00", "16:00-18:00", "18:00-20:00"];

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!list || !list.items || list.items.length === 0) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <ShoppingBag className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">La Mia Lista</h1>
          <p className="text-zinc-400">Nessuna lista della spesa attiva al momento.</p>
          <p className="text-zinc-500 text-sm mt-2">La lista verrà preparata per te dall'amministrazione.</p>
        </div>
        <Footer />
      </div>
    );
  }

  const totalListValue = list.items.reduce((sum, i) => sum + i.price * i.quantity_total, 0);
  const totalOrdered = list.items.reduce((sum, i) => sum + i.price * i.quantity_ordered, 0);
  const progressPercent = Math.round((totalOrdered / totalListValue) * 100);

  return (
    <div className="min-h-screen bg-black" data-testid="special-list-page">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <ShoppingBag className="w-8 h-8 text-green-500" />
          La Mia Lista
        </h1>
        <p className="text-zinc-400 mb-6">Seleziona i prodotti per il tuo prossimo ordine (max €{maxAmount.toFixed(2)})</p>

        {/* Progress bar */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 mb-6">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-zinc-400">Progresso lista</span>
            <span className="text-green-500 font-bold">{progressPercent}%</span>
          </div>
          <div className="w-full bg-zinc-800 rounded-full h-3">
            <div className="bg-green-500 h-3 rounded-full transition-all" style={{ width: `${progressPercent}%` }}></div>
          </div>
          <p className="text-zinc-500 text-xs mt-2">€{totalOrdered.toFixed(2)} ordinati di €{totalListValue.toFixed(2)} totali</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Lista prodotti */}
          <div className="lg:col-span-2 space-y-2">
            {list.items.map(item => {
              const remaining = item.quantity_total - item.quantity_ordered;
              const isDone = remaining <= 0;
              const inCart = miniCart.find(c => c.product_id === item.product_id);

              return (
                <div
                  key={item.product_id}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    isDone ? "bg-zinc-900/50 border-zinc-800/50 opacity-50" : "bg-zinc-900 border-zinc-800"
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {isDone && <Check className="w-4 h-4 text-green-500" />}
                      <p className={`font-medium ${isDone ? "text-zinc-500 line-through" : "text-white"}`}>{item.name}</p>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-sm">
                      <span className="text-green-500 font-semibold">€{item.price.toFixed(2)}</span>
                      <span className="text-zinc-500">
                        {isDone ? "Completato" : `Rimasti: ${remaining} di ${item.quantity_total}`}
                      </span>
                      {inCart && <span className="text-yellow-500 text-xs font-medium">({inCart.quantity} nel carrello)</span>}
                    </div>
                  </div>
                  {!isDone && (
                    <button
                      onClick={() => addToMiniCart(item)}
                      className="bg-green-500 text-black w-8 h-8 rounded-full flex items-center justify-center hover:bg-green-600 shrink-0 ml-3"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Mini carrello */}
          <div className="space-y-4">
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 sticky top-4">
              <h2 className="text-white font-bold mb-3 flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                Ordine ({miniCart.length})
              </h2>

              {miniCart.length === 0 ? (
                <p className="text-zinc-500 text-sm text-center py-4">Aggiungi prodotti dalla lista</p>
              ) : (
                <>
                  <div className="space-y-2 mb-3">
                    {miniCart.map(item => (
                      <div key={item.product_id} className="flex items-center justify-between bg-zinc-800 rounded-lg p-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm truncate">{item.name}</p>
                          <p className="text-zinc-400 text-xs">€{item.price.toFixed(2)} x {item.quantity}</p>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          <button onClick={() => updateMiniCartQty(item.product_id, -1)} className="text-zinc-400 hover:text-white p-1"><Minus className="w-3 h-3" /></button>
                          <span className="text-white font-bold text-sm w-5 text-center">{item.quantity}</span>
                          <button onClick={() => updateMiniCartQty(item.product_id, 1)} className="text-zinc-400 hover:text-white p-1"><Plus className="w-3 h-3" /></button>
                          <button onClick={() => removeMiniCart(item.product_id)} className="text-red-400 p-1 ml-1"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-zinc-700 pt-3 mb-3">
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Totale</span>
                      <span className={`font-bold ${isOverLimit ? "text-red-500" : "text-green-500"}`}>
                        €{miniCartTotal.toFixed(2)}
                      </span>
                    </div>
                    {isOverLimit && (
                      <p className="text-red-400 text-xs mt-1">Supera il limite di €{maxAmount.toFixed(2)}! Rimuovi qualcosa.</p>
                    )}
                  </div>

                  <button
                    onClick={handleOrder}
                    disabled={submitting || isOverLimit || miniCart.length === 0}
                    className="w-full bg-green-500 text-black py-3 rounded-lg font-bold hover:bg-green-600 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <CreditCard className="w-5 h-5" />
                    {submitting ? "Attendi..." : `Paga €${miniCartTotal.toFixed(2)}`}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SpecialListPage;
